import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Grid } from "@mui/material";
import { ValidatorForm } from "react-material-ui-form-validator";
import ValidationRules from "../../../../common/ValidationRules";
import NscDeliveryRangesRow from "./deliveryRangesRow/NscDeliveryRangesRow";
import styles from "./NscDeliveryRanges.module.scss";
import Item from "../../../../shared/item/Item";
import { withTranslation } from "react-i18next";
import ConfirmDialog from "../../../../shared/confirmDialog/ConfirmDialog";
import NscDeliveryRowDisplay from "./deliveryRangesRow/NscDeliveryRowDisplay";
import { InfoIconWithMessage } from "../../../../shared/infoIcon/InfoIcon";
import { DELIVERY_BANDS } from "../../../../common/Constants";
import HasPermissionTo from "../../../../common/HasPermissionTo";
import DeliveryRangesClient from "../../../../shared/clients/DeliveryRangesClient";
import PendingActionClient from "../../../../shared/clients/PendingActionClient";

function NscDeliveryRanges(props) {
  const [ranges, setRanges] = useState([]);
  const [currentNoOfRows, setCurrentNoOfRows] = useState(2);
  const [openDialog, setOpenDialog] = useState(false);
  const [rangesSaved, setRangesSaved] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [deliveryRangesApplied, setDeliveryRangesApplied] = useState(false);

  const defaultNoOfRows = 2;
  const maxNoOfRows = 5;

  const currencySymbol = useMemo(() => {
    return props.localizedInfo && props.localizedInfo.defaultCurrency
      ? props.localizedInfo.defaultCurrency.symbol
      : "NA";
  }, [props.localizedInfo]);

  const distanceUnit = useMemo(() => {
    return props.localizedInfo && props.localizedInfo.defaultDistanceUnit
      ? props.localizedInfo.defaultDistanceUnit
      : "NA";
  }, [props.localizedInfo]);

  const deleteRangeDialogProps = useMemo(() => ({
    title: props.t("nscDeliveryBands.deleteRangeTitle"),
    bodyText: props.t("nscDeliveryBands.deleteRangeBody"),
    confirm: props.t("nscDeliveryBands.deleteRangeConfirm"),
    cancel: props.t("nscDeliveryBands.deleteRangeCancel"),
  }), [props.t]);

  const handleDeleteAction = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleTextOnChange = useCallback((event) => {
    setRanges(prevRanges => {
      const newRanges = JSON.parse(JSON.stringify(prevRanges));

      if (event.target.name === "rangeFrom" + event.target.id) {
        newRanges[event.target.id].from = event.target.value;
      } else if (event.target.name === "rangeTo" + event.target.id) {
        newRanges[event.target.id].to = event.target.value;

        setTimeout(() => {
          if (!isNaN(event.target.value)) {
            autoIncrementNxtRowFromValue(event.target.id);
          }
        }, 0);
      } else if (event.target.name === "rangeValue" + event.target.id) {
        newRanges[event.target.id].value = event.target.value;
      }

      return newRanges;
    });
  }, []);

  const autoIncrementNxtRowFromValue = useCallback((rowId) => {
    setRanges(prevRanges => {
      const newRanges = JSON.parse(JSON.stringify(prevRanges));
      let rowValue = newRanges[rowId].to;
      const currentRow = parseInt(rowId);

      if (currentRow + 1 < currentNoOfRows) {
        if (rowValue !== "") {
          newRanges[currentRow + 1].from = ++rowValue;
        } else {
          newRanges[currentRow + 1].from = null;
        }
      }

      return newRanges;
    });
  }, [currentNoOfRows]);

  const saveDeliveryRanges = useCallback(async (data) => {
    props.hideOrShow(true);
    return await DeliveryRangesClient.saveDeliveryRanges(data, props.user);
  }, [props]);

  const handleSubmit = useCallback(() => {
    let isValidInput = true;
    const rangesToProcess = JSON.parse(JSON.stringify(ranges));
    const length = rangesToProcess.length;

    for (let index = 0; index < length; ++index) {
      const to = rangesToProcess[index].to;
      if (to && isNaN(to) && to.toUpperCase() === "OVER") {
        if (index !== length - 1) {
          rangesToProcess[index].to = null;
          isValidInput = false;
          break;
        } else {
          rangesToProcess[index].to = -1;
        }
      }
      if (rangesToProcess[index].to !== null && rangesToProcess[index].to !== "") {
        rangesToProcess[index].to = parseInt(rangesToProcess[index].to);
      }
      rangesToProcess[index].from = parseInt(rangesToProcess[index].from);
      rangesToProcess[index].value = parseFloat(rangesToProcess[index].value);
    }

    setRanges(rangesToProcess);

    if (isValidInput) {
      saveDeliveryRanges(ranges)
        .then((status) => {
          if (status === 200) {
            props.hideOrShow(false);
            PendingActionClient.removeNscPendingAction(
              props.user,
              DELIVERY_BANDS,
              1
            );
            setRangesSaved(true);
            setDeliveryRangesApplied(true);
          }
        })
        .catch(() => {
          setDeliveryRangesApplied(false);
        });
    }
  }, [ranges, saveDeliveryRanges, props]);

  const fetchRanges = useCallback(() => {
    props.hideOrShow(true);
    DeliveryRangesClient.getDeliveryRanges(props.user)
      .then((fetchedRanges) => {
        if (fetchedRanges.length > 0) {
          setCurrentNoOfRows(fetchedRanges.length);
          setRanges(fetchedRanges);
          setRangesSaved(true);
        } else {
          setRanges([
            { from: "0", to: "", value: "" },
            { from: "", to: "", value: "" },
          ]);
        }
      })
      .finally(() => {
        props.hideOrShow(false);
        setDataLoaded(true);
      });
  }, [props]);

  const addRow = useCallback(() => {
    if (currentNoOfRows === maxNoOfRows) {
      return;
    }

    setRanges(prevRanges => {
      const newRanges = JSON.parse(JSON.stringify(prevRanges));
      newRanges.push({ from: "", to: "", value: "" });

      let previousRowToVal = newRanges[newRanges.length - 2].to;
      if (
        previousRowToVal !== null &&
        previousRowToVal !== "" &&
        !isNaN(previousRowToVal)
      ) {
        newRanges[newRanges.length - 1].from = ++previousRowToVal;
      }

      return newRanges;
    });

    setCurrentNoOfRows(prev => prev + 1);
  }, [currentNoOfRows, maxNoOfRows]);

  const deleteRow = useCallback(() => {
    if (currentNoOfRows === defaultNoOfRows) {
      return;
    }

    setRanges(prevRanges => {
      const newRanges = JSON.parse(JSON.stringify(prevRanges));
      newRanges.pop();
      return newRanges;
    });

    setCurrentNoOfRows(prev => prev - 1);
    setOpenDialog(false);
  }, [currentNoOfRows, defaultNoOfRows]);

  useEffect(() => {
    ValidatorForm.addValidationRule("checkingRows", (fromVal, currentRow) => {
      let isValid = true;
      if (currentRow < 1 || ranges[currentRow - 1]?.to === null) {
        isValid = true;
      } else if (
        ranges[currentRow - 1]?.to === "" ||
        isNaN(ranges[currentRow - 1]?.to) ||
        fromVal === ""
      ) {
        isValid = true;
      } else if (
        parseInt(fromVal) !== parseInt(ranges[currentRow - 1]?.to) + 1
      ) {
        isValid = false;
      }

      return isValid;
    });

    ValidatorForm.addValidationRule("shouldBeZero", (value, index) => {
      // eslint-disable-next-line
      if (index != 0) return true;
      const re = /^[0\b]+$/;
      let isValid = true;
      if (value !== "" && !re.test(value)) {
        isValid = false;
      }
      return isValid;
    });

    ValidationRules();
    fetchRanges();
  }, [fetchRanges]);

  return (
    dataLoaded && (
      <section className="pageWrapper">
        <div className="pagePaddingLeft pagePaddingTop">
          <InfoIconWithMessage
            message={props.t("nscDeliveryBands.subheader")}
          />
        </div>

        <ConfirmDialog
          open={openDialog}
          dialogProps={deleteRangeDialogProps}
          onConfirm={deleteRow}
          onCancel={() => setOpenDialog(false)}
          onClose={() => setOpenDialog(false)}
          boldTitle={false}
          {...props}
        />

        <div className="pagePaddingTop">
          <Grid
            container
            spacing={0}
            className={`${styles.divider} pagePaddingLeft`}
          >
            <Grid item xs={3} className={styles.rowHeader}>
              <div className={styles.headerWrap}>
                <Item
                  value={
                    props.t("nscDeliveryBands.bands") +
                    "  (" +
                    distanceUnit +
                    ")"
                  }
                />
              </div>
            </Grid>
            <Grid
              item
              style={{ justifyContent: "flex-end" }}
              xs={5}
              className={styles.rowHeader}
            >
              <div className={styles.headerWrap}>
                <Item
                  value={
                    props.t("nscDeliveryBands.charge") +
                    "  (" +
                    currencySymbol +
                    ")"
                  }
                />
              </div>
            </Grid>
            <Grid item xs={4} />
          </Grid>
        </div>

        {rangesSaved ? (
          <div>
            {ranges.map((range, index) => {
              return (
                <NscDeliveryRowDisplay
                  id="deliveryDisplay"
                  range={range}
                  key={index}
                />
              );
            })}
            <div className={styles.pricesApplied}>
              <span>{props.t("nscDeliveryBands.applied")}</span>
            </div>
          </div>
        ) : (
          <HasPermissionTo
            perform={["deliveryRanges:write"]}
            permissions={props.user.permissions.rolePermissions}
            render={() => (
              <ValidatorForm onSubmit={handleSubmit}>
                {ranges.map((item, index) => {
                  return (
                    <NscDeliveryRangesRow
                      key={index}
                      dataObjItem={item}
                      action={handleTextOnChange}
                      deleteAction={handleDeleteAction}
                      defaultNoOfRows={defaultNoOfRows}
                      currentNoOfRows={currentNoOfRows}
                      currentRow={index}
                    />
                  );
                })}

                <div>
                  {currentNoOfRows === maxNoOfRows ? (
                    <p className={styles.max}>
                      {props.t("nscDeliveryBands.maxRows")}
                    </p>
                  ) : (
                    <Button
                      id="addDeliveryRangeBtn"
                      className={styles.add}
                      onClick={addRow}
                      component="button"
                    >
                      {props.t("nscDeliveryBands.add")}
                    </Button>
                  )}
                </div>
                <div className={styles.actionSection}>
                  <Button
                    data-testid="APPLY-BUTTON"
                    type="submit"
                    className={styles.saveBtn}
                    component="button"
                    disabled={deliveryRangesApplied}
                  >
                    {props.t("nscDeliveryBands.submit")}
                  </Button>
                </div>
              </ValidatorForm>
            )}
          />
        )}
      </section>
    )
  );
}

export default withTranslation("emp")(NscDeliveryRanges);
